import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { Fragment, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import useUser from "../../../../../hooks/useUser";
import DownloadInvoice, { canDownloadInvoice } from "../DownloadInvoice";
import FinishMeetingButton, {
  canFinishAppointment,
} from "../FinishMeetingButton";
import AcceptAppointment, { canAcceptAppointment } from "./AcceptAppointment";
import CancelAppointment, { canCancelAppointment } from "./CancelAppointment";
import DoctorsLetterActions, {
  canViewDoctorsLetter,
} from "./DoctorsLetterActions";
import HeaderIconButton from "./HeaderIconButton";
import RevertAppointment, { canRevertAppointment } from "./RevertAppointment";
import TreatmentEditing, { canEditTreatment } from "./TreatmentEditing";
import NonSystemStatusActions, {
  canChangeNonSystemStatus,
} from "./NonSystemStatusActions";
import useAppointmentGeneral from "../../hooks/useAppointmentGeneral";

/** The actions menu button, hidden when none of its actions apply to the current user. */
export default function AppointmentActions({ appointment }) {
  const { t } = useTranslation();
  const { data } = useAppointmentGeneral();
  const invoices = data?.invoices;
  const clinicType = data?.clinicType;
  const { roles } = useUser();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const [open, setOpen] = useState(false);

  if (!hasActions(appointment, invoices, clinicType, roles, isPermissionAllowed)) {
    return null;
  }

  return (
    <Fragment>
      <HeaderIconButton
        title={t("Actions")}
        onClick={() => setOpen(true)}
        testId={"appointment.details.actions-button"}
      >
        <MoreVertIcon />
      </HeaderIconButton>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth={"xs"}
        data-testid={"appointment.details.actions-dialog"}
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <ActionsDialogHeader onClose={() => setOpen(false)} />
        <Divider sx={{ opacity: 0.6 }} />
        <DialogContent sx={{ p: 0 }}>
          <AcceptAppointment appointment={appointment} />
          <NonSystemStatusActions appointment={appointment} clinicType={clinicType} />
          <FinishMeetingButton appointment={appointment} />
          <RevertAppointment
            status={appointment?.status}
            isConfirmed={appointment?.confirmedAt}
          />
          <DoctorsLetterActions appointment={appointment} />
          <TreatmentEditing initValue={appointment} />
          <DownloadInvoice invoicesList={invoices} />
          <CancelAppointment appointment={appointment} clinicType={clinicType} />
        </DialogContent>
      </Dialog>
    </Fragment>
  );
}

function hasActions(appointment, invoices, clinicType, roles, isPermissionAllowed) {
  return (
    canAcceptAppointment(appointment, roles, isPermissionAllowed) ||
    canChangeNonSystemStatus(appointment, clinicType, roles) ||
    canFinishAppointment(appointment, roles, isPermissionAllowed) ||
    canRevertAppointment({
      status: appointment?.status,
      isConfirmed: appointment?.confirmedAt,
      roles,
    }) ||
    canViewDoctorsLetter(appointment) ||
    canEditTreatment(appointment, roles) ||
    canDownloadInvoice(invoices) ||
    canCancelAppointment(appointment, roles, clinicType)
  );
}

function ActionsDialogHeader({ onClose }) {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
      }}
    >
      <Typography variant="h6" component="h2" fontWeight={600}>
        {t("AppointmentActions")}
      </Typography>
      <IconButton
        onClick={onClose}
        title={t("Close")}
        data-testid={"appointment.details.actions-dialog.close-button"}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
}
